import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { useLoginContext } from "../Contexts/LoginContext.js";
import api from "../services/api.js";
import { getCachedUsers, setCachedUsers } from "../utils/userCache.jsx";

import FeedbackModal from "../components/feedbackmodel.jsx";
import UserIcon from "../components/userIcon.jsx";
import DashboardSkeleton from "../components/DashboardSkeleton.jsx";

import {
  FaArrowRight,
  FaEye,
  FaLanguage,
  FaRegCalendarAlt,
  FaRegCommentDots,
  FaUserFriends,
  FaUsers,
  FaUserPlus,
  FaWallet,
} from "react-icons/fa";
import { GiBuffaloHead, GiCow } from "react-icons/gi";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper";

const ImageIcon = ({ src, className }) => (
  <img
    src={src}
    alt=""
    className={`object-contain ${className || "h-6 w-6"}`}
    aria-hidden="true"
  />
);

const ReceiveIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v7" />
    <path d="M8.5 11l3.5 3.5L15.5 11" />
  </svg>
);

const PayIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16.5v-7" />
    <path d="M8.5 13l3.5-3.5L15.5 13" />
  </svg>
);

const LedgerTile = ({
  to,
  icon,
  title,
  subtitle,
  iconClass,
  iconBgClass,
  compact = false,
}) => (
  <Link
    to={to}
    aria-label={subtitle ? `${title}. ${subtitle}` : title}
    className={`group flex items-center rounded-[20px] bg-white text-left shadow-[0_8px_24px_rgba(20,35,58,0.06)] ring-1 ring-[#E9EDF3] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(20,35,58,0.10)] active:scale-[0.98] motion-reduce:transform-none ${focusRing} ${
      compact ? "min-h-[82px] gap-3 px-3.5 py-3" : "min-h-[92px] gap-3.5 px-4 py-4"
    }`}
  >
    <span
      className={`flex flex-shrink-0 items-center justify-center rounded-2xl ${iconBgClass} ${iconClass} ${
        compact ? "h-11 w-11 text-lg" : "h-12 w-12 text-xl"
      }`}
      aria-hidden="true"
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[14px] font-bold leading-snug text-ink">
        {title}
      </span>
      {subtitle && (
        <span className="mt-1 block text-[11.5px] font-medium leading-snug text-muted">
          {subtitle}
        </span>
      )}
    </span>
    {!compact && (
      <FaArrowRight
        className="flex-shrink-0 text-[11px] text-[#98A2B3] transition-transform group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    )}
  </Link>
);

const SectionHeader = ({ id, title }) => (
  <div className="mb-3.5 flex items-end justify-between">
    <h2 id={id} className="text-[17px] font-extrabold tracking-[-0.015em] text-ink">
      {title}
    </h2>
  </div>
);

const RateRow = ({ to, icon, title, subtitle }) => (
  <Link
    to={to}
    className={`group flex min-h-[70px] items-center justify-between gap-3 border-b border-line/80 px-4 py-3.5 last:border-b-0 hover:bg-paper/60 active:bg-primary/[0.05] ${focusRing}`}
  >
    <span className="flex min-w-0 items-center gap-3">
      <span
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-lg text-primary"
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] font-bold text-ink">
          {title}
        </span>
        <span className="mt-0.5 block text-[11.5px] text-muted">{subtitle}</span>
      </span>
    </span>
    <FaArrowRight
      className="mr-1 flex-shrink-0 text-xs text-[#98A2B3] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary motion-reduce:transform-none"
      aria-hidden="true"
    />
  </Link>
);

const DashBoard = () => {
  const { t, i18n } = useTranslation();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [showFeedbackInvite, setShowFeedbackInvite] = useState(false);
  const [hasCustomers, setHasCustomers] = useState(null);
  const { role, loggedIn, loading, userName } = useLoginContext();
  const navigate = useNavigate();

  const isHindi = i18n.resolvedLanguage?.toLowerCase().startsWith("hi");
  const language = isHindi ? "hi" : "en";
  const locale = isHindi ? "hi-IN" : "en-IN";

  useEffect(() => {
    if (!loading && !loggedIn) navigate("/");
  }, [loading, loggedIn, navigate]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (!loggedIn || role !== "admin") {
      setHasCustomers(null);
      return undefined;
    }

    const cachedUsers = getCachedUsers();
    if (Array.isArray(cachedUsers)) {
      setHasCustomers(cachedUsers.length > 0);
      return undefined;
    }

    let active = true;
    api
      .get("/users")
      .then((response) => {
        if (!active || !Array.isArray(response.data)) return;
        setCachedUsers(response.data);
        setHasCustomers(response.data.length > 0);
      })
      .catch((error) => {
        console.error("Unable to check customer setup:", error);
      });

    return () => {
      active = false;
    };
  }, [loggedIn, role]);

  useEffect(() => {
    let timer;

    const checkFeedbackPrompt = async () => {
      if (!loggedIn) return;

      try {
        const response = await api.get("/prompt");
        if (response.data?.showFeedback) setShowFeedbackInvite(true);
      } catch (error) {
        console.error("Unable to check feedback prompt:", error);
      }
    };

    if (loggedIn) timer = setTimeout(checkFeedbackPrompt, 12000);
    return () => clearTimeout(timer);
  }, [loggedIn]);

  const handleCloseFeedback = () => setIsFeedbackOpen(false);

  const handleDismissFeedback = () => {
    setIsFeedbackOpen(false);
    setShowFeedbackInvite(false);
  };

  const handleSubmitFeedback = async ({ rating, feedback }) => {
    try {
      await api.post("/api/feedback", { rating, feedback });
      setShowFeedbackInvite(false);
      toast.success(t("feedback.success"));
    } catch (error) {
      toast.error(t("feedback.error"));
      throw error;
    }
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(isHindi ? "en" : "hi");
  };

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }).format(new Date()),
    [locale]
  );

  const firstName = userName?.trim().split(/\s+/)[0];
  const welcomeText = firstName
    ? t("dashboard.welcome_name", { name: firstName })
    : t("dashboard.welcome");

  const roleLabel =
    role === "admin"
      ? t("dashboard.admin")
      : role === "associated user"
      ? t("dashboard.assoc")
      : t("dashboard.user");

  const needsCustomerSetup = role === "admin" && hasCustomers === false;

  const { people, milkEntry, subEntries, insights, prices } = useMemo(() => {
    const peopleGroup =
      role === "admin"
        ? [
            {
              id: "customers",
              title: t("actions.customers"),
              subtitle: t("actions.manage"),
              icon: <FaUsers />,
              path: "/customers",
              iconClass: "text-primary",
              iconBgClass: "bg-primary/10",
            },
            {
              id: "today",
              title: t("actions.active_today"),
              subtitle: t("actions.check"),
              icon: <FaUserFriends />,
              path: "/todays-customers",
              iconClass: "text-primary",
              iconBgClass: "bg-primary/10",
            },
          ]
        : [];

    const milkItem = {
      title: t("actions.milk_entry"),
      subtitle: t("actions.milk_subtitle"),
      icon: (
        <ImageIcon
          src="/icons/milk-can.png"
          className="h-8 w-8 brightness-0 invert"
        />
      ),
      path: "/entries/milk-entry",
    };

    const subItems = [
      {
        id: "ghee",
        title: t("actions.ghee"),
        icon: <ImageIcon src="/icons/gheeIcon.png" className="h-5 w-5" />,
        path: "/entries/ghee-entry",
        iconClass: "text-amber",
        iconBgClass: "bg-amber/10",
      },
      {
        id: "feed",
        title: t("actions.feed"),
        icon: <ImageIcon src="/icons/feed.png" className="h-5 w-5" />,
        path: "/entries/feed-entry",
        iconClass: "text-olive",
        iconBgClass: "bg-olive/10",
      },
      {
        id: "receive",
        title: t("actions.receive"),
        icon: <ReceiveIcon className="h-5 w-5" />,
        path: "/entries/receive-money",
        iconClass: "text-credit",
        iconBgClass: "bg-credit/10",
      },
      {
        id: "pay",
        title: t("actions.pay"),
        icon: <PayIcon className="h-5 w-5" />,
        path: "/entries/give-money",
        iconClass: "text-debit",
        iconBgClass: "bg-debit/10",
      },
    ];

    const insightGroup = [
      {
        id: "view",
        title: t("actions.view_entries"),
        subtitle: t("actions.view_subtitle"),
        icon: <FaEye />,
        path: "/view-entries",
        iconClass: "text-primary",
        iconBgClass: "bg-primary/10",
      },
      {
        id: "balance",
        title: t("actions.wallet"),
        subtitle: t("actions.wallet_subtitle"),
        icon: <FaWallet />,
        path: "/balance",
        iconClass: "text-credit",
        iconBgClass: "bg-credit/10",
      },
    ];

    const priceGroup =
      role !== "associated user"
        ? [
            {
              id: "cow",
              title: t("actions.cow_rate"),
              icon: <GiCow />,
              path: "/price/cow_Milk_Prices",
            },
            {
              id: "buffalo",
              title: t("actions.buffalo_rate"),
              icon: <GiBuffaloHead />,
              path: "/price/buffalo_Milk_Prices",
            },
          ]
        : [];

    return {
      people: peopleGroup,
      milkEntry: milkItem,
      subEntries: subItems,
      insights: insightGroup,
      prices: priceGroup,
    };
  }, [role, t]);

  if (loading) return <DashboardSkeleton />;
  if (!loggedIn) return null;

  return (
    <div className="min-h-screen w-full bg-paper pb-28 font-sans text-ink">
      <Helmet>
        <title>{t("dashboard.title")}</title>
        <meta name="theme-color" content="#F5F7FB" />
      </Helmet>

      <header className="sticky top-0 z-30 bg-white/90 shadow-[0_1px_0_rgba(20,35,58,0.06)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3 px-4 py-3.5">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10">
              <img
                src="/images/logo.png"
                alt={t("dashboard.logo_alt")}
                className="h-9 w-9 object-contain"
              />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-[16px] font-extrabold leading-tight tracking-[-0.015em] text-ink">
                {t("dashboard.title")}
              </h1>
              <div className="mt-1 flex items-center gap-1.5 text-[10.5px] font-semibold text-muted">
                <span className="h-1.5 w-1.5 rounded-full bg-credit" aria-hidden="true" />
                <span className="truncate">{roleLabel}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={toggleLanguage}
              className={`flex h-11 items-center gap-1.5 rounded-2xl bg-paper px-3 text-[11px] font-bold text-primary transition-colors hover:bg-primary/10 active:bg-primary/15 ${focusRing}`}
              aria-label={
                isHindi
                  ? t("language.switch_to_english")
                  : t("language.switch_to_hindi")
              }
              title={t("language.change")}
            >
              <FaLanguage className="text-sm" aria-hidden="true" />
              <span>{isHindi ? "EN" : "हिंदी"}</span>
            </button>
            <UserIcon role={role} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-7 px-4 pb-4 pt-5">
        <section
          aria-labelledby="dashboard-welcome"
          className="relative overflow-hidden rounded-[28px] bg-primary p-5 text-white shadow-[0_16px_36px_rgba(15,118,110,0.22)]"
        >
          <div
            className="absolute -right-10 -top-14 h-40 w-40 rounded-full bg-white/[0.08]"
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-14 right-16 h-28 w-28 rounded-full border-[18px] border-white/[0.05]"
            aria-hidden="true"
          />
          <div className="relative">
            <p className="mb-3 flex items-center gap-2 text-[11.5px] font-semibold capitalize text-white/70">
              <FaRegCalendarAlt className="text-white/85" aria-hidden="true" />
              <span>{today}</span>
            </p>
            <h2
              id="dashboard-welcome"
              className="text-[23px] font-extrabold leading-tight tracking-[-0.025em] text-white"
            >
              {welcomeText}
            </h2>
            <p className="mt-2 max-w-[310px] text-[13px] font-medium leading-relaxed text-white/70">
              {needsCustomerSetup
                ? t("entrySetup.dashboard_body")
                : t("dashboard.prompt")}
            </p>

            {role !== "associated user" && (
              <Link
                to={needsCustomerSetup ? "/addCustomer" : milkEntry.path}
                className={`mt-5 flex min-h-[62px] items-center gap-3 rounded-[20px] bg-white p-2.5 pr-4 text-ink shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition active:scale-[0.985] ${focusRing}`}
                aria-label={
                  needsCustomerSetup
                    ? t("entrySetup.add_customer")
                    : `${milkEntry.title}. ${milkEntry.subtitle}`
                }
              >
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                  {needsCustomerSetup ? (
                    <FaUserPlus className="text-lg text-primary" aria-hidden="true" />
                  ) : (
                    <ImageIcon src="/icons/milk-can.png" className="h-7 w-7" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14.5px] font-extrabold">
                    {needsCustomerSetup
                      ? t("entrySetup.add_customer")
                      : milkEntry.title}
                  </span>
                  <span className="mt-0.5 block truncate text-[11.5px] font-medium text-muted">
                    {needsCustomerSetup
                      ? t("entrySetup.empty_badge")
                      : milkEntry.subtitle}
                  </span>
                </span>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white">
                  <FaArrowRight className="text-xs" aria-hidden="true" />
                </span>
              </Link>
            )}
          </div>
        </section>

        {role !== "associated user" && !needsCustomerSetup && (
          <section aria-labelledby="operations-heading">
            <SectionHeader id="operations-heading" title={t("sections.operations")} />

            <div className="grid grid-cols-2 gap-3">
              {subEntries.map((item) => (
                <LedgerTile
                  key={item.id}
                  to={item.path}
                  icon={item.icon}
                  title={item.title}
                  iconClass={item.iconClass}
                  iconBgClass={item.iconBgClass}
                  compact
                />
              ))}
            </div>
          </section>
        )}

        {people.length > 0 && (
          <section aria-labelledby="people-heading">
            <SectionHeader id="people-heading" title={t("sections.people")} />
            <div className="grid grid-cols-1 gap-3 min-[460px]:grid-cols-2">
              {people.map((item) => (
                <LedgerTile
                  key={item.id}
                  to={item.path}
                  icon={item.icon}
                  title={item.title}
                  subtitle={item.subtitle}
                  iconClass={item.iconClass}
                  iconBgClass={item.iconBgClass}
                />
              ))}
            </div>
          </section>
        )}

        <section aria-labelledby="overview-heading">
          <SectionHeader id="overview-heading" title={t("sections.overview")} />
          <div className="grid grid-cols-1 gap-3 min-[460px]:grid-cols-2">
            {insights.map((item) => (
              <LedgerTile
                key={item.id}
                to={item.path}
                icon={item.icon}
                title={item.title}
                subtitle={item.subtitle}
                iconClass={item.iconClass}
                iconBgClass={item.iconBgClass}
              />
            ))}
          </div>
        </section>

        {prices.length > 0 && (
          <section aria-labelledby="rates-heading">
            <SectionHeader id="rates-heading" title={t("sections.rates")} />
            <div className="overflow-hidden rounded-[22px] bg-white shadow-[0_8px_24px_rgba(20,35,58,0.06)] ring-1 ring-[#E9EDF3]">
              {prices.map((item) => (
                <RateRow
                  key={item.id}
                  to={item.path}
                  icon={item.icon}
                  title={item.title}
                  subtitle={t("actions.rate_subtitle")}
                />
              ))}
            </div>
          </section>
        )}

        {showFeedbackInvite && (
          <aside className="rounded-[22px] bg-primary/[0.07] p-4 ring-1 ring-primary/10">
            <div className="flex items-start gap-3">
              <span
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-primary shadow-sm"
                aria-hidden="true"
              >
                <FaRegCommentDots />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="text-[14px] font-bold text-ink">
                  {t("feedback.invite_title")}
                </h2>
                <p className="mt-1 text-[12px] leading-relaxed text-muted">
                  {t("feedback.invite_body")}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFeedbackOpen(true)}
                    className={`min-h-[42px] rounded-xl bg-primary px-4 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-primary/90 ${focusRing}`}
                  >
                    {t("feedback.open")}
                  </button>
                  <button
                    type="button"
                    onClick={handleDismissFeedback}
                    className={`min-h-[42px] rounded-xl px-4 py-2 text-[12px] font-semibold text-muted transition-colors hover:bg-white/70 ${focusRing}`}
                  >
                    {t("feedback.not_now")}
                  </button>
                </div>
              </div>
            </div>
          </aside>
        )}
      </main>

      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={handleCloseFeedback}
        onSubmit={handleSubmitFeedback}
      />
    </div>
  );
};

export default DashBoard;
